import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Target, X } from 'lucide-react';
import { Turn, POSITION_ZONES } from '../types/curling';
import { endTypeFromEndNumber } from '../utils/teeLineEstimate';

export interface ShotResultRecorderAppliedValues {
  actualPosition: number; // 0-16
  curl: number;            // signed feet, curl-from-broom
  turn: Turn;
}

interface ShotResultRecorderProps {
  // Which end this shot belongs to — decides which end of the sheet the hog
  // line renders at, same as the Shot Visualizer, so the two diagrams for a
  // given shot always agree on orientation.
  endNumber: number;
  // Feet from center, already established by the call — fixed here, not draggable.
  broomPosition: number;
  // The call's own expected curl and zone, so the rock defaults to wherever
  // it was supposed to stop rather than dead on the broom — that's the null
  // hypothesis for "what actually happened" (see the rockPos initializer).
  expectedCurl: number;
  targetZone: number;
  className?: string;
  // Records the result and returns to the Record Shot page.
  onRecord: (values: ShotResultRecorderAppliedValues) => void;
  // Records the result and goes straight into the main page's Analyze Shot
  // flow, same as pressing Analyze Shot there right after a manual entry.
  onRecordAndAnalyze: (values: ShotResultRecorderAppliedValues) => void;
}

// Curls the rock to the right of the broom, same convention as everywhere
// else in the app (see turnToDirection in utils/teeLineEstimate).
const curlToTurn = (curlValue: number): Turn => (curlValue >= 0 ? 'clockwise' : 'counter-clockwise');

// Same fixed-weight hit types as the Shot Visualizer's own mode strip. Unlike
// a draw, none of them land at a distinguishable spot in the house, so — also
// like the Shot Visualizer — picking one doesn't involve placing the rock
// row-by-row; it just fixes the zone directly (see resolvedZone) and the rock
// is shown past the back line as a generic "ran through" marker.
const HIT_ZONES: Array<{ zone: number; abbr: string; label: string }> = [
  { zone: 11, abbr: 'Hk', label: 'Hack' },
  { zone: 12, abbr: 'Brd', label: 'Board' },
  { zone: 13, abbr: 'Ctl', label: 'Control' },
  { zone: 14, abbr: 'Nrm', label: 'Normal' },
  { zone: 15, abbr: 'Fm', label: 'Firm' },
  { zone: 16, abbr: 'Pl', label: 'Peel' },
];

/**
 * Trigger button + modal for recording where a thrown rock actually finished —
 * the Shot Visualizer's sibling for results rather than calls. Same house
 * scale and overall size as the Shot Visualizer (so the two diagrams read
 * consistently and neither dwarfs the other), plus a Hogged band above the
 * hog line for the one real outcome the Shot Visualizer never needs to plan
 * for. The broom is fixed (drawn only as a reference for the called line);
 * the rock is the only draggable element, free in X (curl) always and in Y
 * (zone) while Draw is selected — a hit type fixes Y automatically, the same
 * way it fixes weight in the Shot Visualizer.
 */
export function ShotResultRecorder({
  endNumber, broomPosition, expectedCurl, targetZone, className, onRecord, onRecordAndAnalyze,
}: ShotResultRecorderProps) {
  const [show, setShow] = useState(false);
  // Defaults to whichever mode matches the call: a hit-type call is most
  // likely to still land as that hit type, a draw call (zones 0-10) starts
  // in Draw so the rock is immediately draggable.
  const [shotMode, setShotMode] = useState<'draw' | number>(targetZone >= 11 ? targetZone : 'draw');

  const W = 340;
  const H = 500;
  const centerX = W / 2;
  const hogLineY = 40;
  const teeLineY = 300;
  const PX_PER_FOOT = 20;
  const radii = {
    twelve: 6 * PX_PER_FOOT,
    eight: 4 * PX_PER_FOOT,
    four: 2 * PX_PER_FOOT,
    button: 0.5 * PX_PER_FOOT,
  };
  const backLineY = teeLineY + radii.twelve;
  const houseFrontY = teeLineY - radii.twelve;
  const boardsY = H - 30;
  // Flush with the sheet background's own top edge (y=20 below), so the band
  // never overflows it the way a smaller hoggedBandTop used to.
  const hoggedBandTop = 20;
  // Every hit type shares one generic "ran through" row, roughly midway
  // between the back line and the boards — there's no spot in the house a
  // hit weight lands at the way a draw does, so unlike zones 0-10 there's
  // nothing more specific to place it at.
  const hitZoneY = backLineY + (boardsY - backLineY) / 2;

  // Same away/home flip as the Shot Visualizer, keyed off the same end
  // number — so whichever direction that diagram drew the hog line, this one
  // agrees. Every y below is computed in logical (unflipped) space, same as
  // the Shot Visualizer's own drawAnchors/broomPos.y, and only passed through
  // Y() at render time.
  const flip = endTypeFromEndNumber(endNumber) === 'away';
  const Y = (y: number) => (flip ? H - y : y);

  // Same anchor points as the Shot Visualizer's own drawAnchors, so the two
  // diagrams' zone rows line up, plus a Hogged band above the hog line. Only
  // zones 0-10 are ever resolved from a dragged Y (see resolvedZone) — 11-16
  // are looked up here only to seed the rock's initial position when the
  // call itself was a hit type.
  const ZONE_Y: Record<number, number> = {
    0: (hoggedBandTop + hogLineY) / 2,
    1: hogLineY + (houseFrontY - hogLineY) * 0.25,
    2: hogLineY + (houseFrontY - hogLineY) * 0.5,
    3: hogLineY + (houseFrontY - hogLineY) * 0.75,
    4: houseFrontY,
    5: teeLineY - radii.eight,
    6: teeLineY - radii.four,
    7: teeLineY,
    8: teeLineY + radii.four,
    9: teeLineY + radii.eight,
    10: backLineY,
  };
  HIT_ZONES.forEach(z => { ZONE_Y[z.zone] = hitZoneY; });

  // Draw-only lookup (0-10): dragging in Draw mode should never resolve to a
  // hit zone just because the rock was dragged down near hitZoneY — hit
  // zones are only ever reached by picking their button above.
  const nearestDrawZoneFromY = (y: number): number => {
    let closestZone = 0;
    let closestDistance = Infinity;
    for (let zone = 0; zone <= 10; zone++) {
      const distance = Math.abs(ZONE_Y[zone] - y);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestZone = zone;
      }
    }
    return closestZone;
  };

  const broomRenderX = centerX + broomPosition * PX_PER_FOOT;

  // Defaults to where the call's own rock sits — broom plus the call's
  // expected curl, at the call's target zone — since that's where the shot
  // should have finished if it had gone exactly as planned. The user drags
  // from there to record where it actually landed instead.
  const [rockPos, setRockPos] = useState({
    x: Math.min(Math.max(broomRenderX + expectedCurl * PX_PER_FOOT, 25), W - 25),
    y: ZONE_Y[targetZone] ?? teeLineY,
  });
  const isDraggingRef = useRef(false);

  const handleRockPointerDown = (e: React.PointerEvent<SVGGElement>) => {
    e.stopPropagation();
    isDraggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleRockPointerMove = (e: React.PointerEvent<SVGGElement>) => {
    if (!isDraggingRef.current) return;
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
    const x = Math.min(Math.max(svgP.x, 25), W - 25);
    const renderY = Math.min(Math.max(svgP.y, hoggedBandTop + 10), H - 25);
    // Y keeps tracking the drag even in a hit mode (so switching back to Draw
    // resumes from somewhere sensible) — it's just not what's rendered or
    // resolved to a zone while a hit type is selected (see renderRockY/
    // resolvedZone below).
    setRockPos({ x, y: flip ? H - renderY : renderY });
  };

  const handleRockPointerUp = (e: React.PointerEvent<SVGGElement>) => {
    isDraggingRef.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // A hit type fixes the zone directly and the rock is shown past the back
  // line automatically — same relationship the Shot Visualizer has between
  // shotMode and its own resolvedZone/weight, just for where the rock is
  // drawn instead of the broom's weight estimate.
  const renderRockY = shotMode === 'draw' ? rockPos.y : hitZoneY;
  const resolvedZone = shotMode === 'draw' ? nearestDrawZoneFromY(rockPos.y) : shotMode;
  const resolvedCurl = (rockPos.x - broomRenderX) / PX_PER_FOOT;
  const resolvedTurn = curlToTurn(resolvedCurl);

  const handleRecord = () => {
    onRecord({ actualPosition: resolvedZone, curl: resolvedCurl, turn: resolvedTurn });
    setShow(false);
  };

  const handleRecordAndAnalyze = () => {
    onRecordAndAnalyze({ actualPosition: resolvedZone, curl: resolvedCurl, turn: resolvedTurn });
    setShow(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setShow(true)}
        className={`flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-400 text-white rounded-lg transition-colors text-sm font-medium ${className ?? ''}`}
      >
        <Target className="w-4 h-4" />
        Record Actual Result
      </button>

      {show && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShow(false)}
        >
          <div
            className="bg-slate-900 border border-white/15 rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-xl font-bold text-white">Shot Result</h2>
              <button onClick={() => setShow(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Same mode strip as the Shot Visualizer — Draw for anything landing
                in the house, a hit type when it didn't. */}
            <div className="flex gap-1.5 mb-1">
              <button
                type="button"
                onClick={() => setShotMode('draw')}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  shotMode === 'draw' ? 'bg-blue-500 text-white' : 'bg-white/10 text-slate-300 hover:bg-white/15'
                }`}
              >
                Draw
              </button>
              {HIT_ZONES.map(z => (
                <button
                  type="button"
                  key={z.zone}
                  onClick={() => setShotMode(z.zone)}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    shotMode === z.zone ? 'bg-amber-500 text-white' : 'bg-white/10 text-slate-300 hover:bg-white/15'
                  }`}
                >
                  {z.abbr}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-center gap-2 mb-3 text-sm">
              <span className="text-slate-400">Landed:</span>
              <span className="font-bold text-white">{POSITION_ZONES[resolvedZone]?.label ?? resolvedZone}</span>
              <span className="text-slate-500">·</span>
              <span className="font-bold" style={{ color: resolvedTurn === 'clockwise' ? '#f87171' : '#60a5fa' }}>
                {resolvedCurl >= 0 ? '+' : ''}{resolvedCurl.toFixed(1)} ft
              </span>
            </div>

            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
              <rect x={20} y={20} width={W - 40} height={H - 40} rx={8} fill="#0f2942" stroke="rgba(255,255,255,0.15)" />

              {/* Hogged band — a real outcome for a thrown rock, even though it's never a call.
                  y is the min of the two flipped endpoints, not hogLineY itself: a rect (unlike a
                  line or circle) needs an actual top-left corner, so it doesn't automatically land
                  right when the endpoints swap order under the flip. */}
              <rect x={20} y={Math.min(Y(hoggedBandTop), Y(hogLineY))} width={W - 40} height={hogLineY - hoggedBandTop} fill="rgba(239,68,68,0.12)" style={{ pointerEvents: 'none' }} />
              {/* Anchored to the hog line itself and nudged into the band — into the
                  band means opposite directions depending on the flip, since the
                  band's other edge swaps sides too. */}
              <text x={W - 24} y={Y(hogLineY) + (flip ? 6 : -6)} textAnchor="end" fontSize={9} fill="#fca5a5" style={{ pointerEvents: 'none' }}>Hogged</text>

              {/* House rings, centered on the button at the tee line */}
              <circle cx={centerX} cy={Y(teeLineY)} r={radii.twelve} fill="#2563eb" fillOpacity={0.25} stroke="#3b82f6" strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
              <circle cx={centerX} cy={Y(teeLineY)} r={radii.eight} fill="#0f2942" stroke="#e2e8f0" strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
              <circle cx={centerX} cy={Y(teeLineY)} r={radii.four} fill="#dc2626" fillOpacity={0.35} stroke="#ef4444" strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
              <circle cx={centerX} cy={Y(teeLineY)} r={radii.button} fill="#ef4444" style={{ pointerEvents: 'none' }} />

              <line x1={20} y1={Y(hogLineY)} x2={W - 20} y2={Y(hogLineY)} stroke="#f87171" strokeWidth={2.5} style={{ pointerEvents: 'none' }} />
              <text x={24} y={Y(hogLineY) + 14} fontSize={9} fill="#f87171" style={{ pointerEvents: 'none' }}>Hog Line</text>

              <line x1={20} y1={Y(teeLineY)} x2={W - 20} y2={Y(teeLineY)} stroke="rgba(255,255,255,0.35)" strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
              <text x={24} y={Y(teeLineY) + 14} fontSize={9} fill="rgba(255,255,255,0.5)" style={{ pointerEvents: 'none' }}>Tee</text>

              <line x1={20} y1={Y(backLineY)} x2={W - 20} y2={Y(backLineY)} stroke="rgba(255,255,255,0.35)" strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
              <text x={24} y={Y(backLineY) + 14} fontSize={9} fill="rgba(255,255,255,0.5)" style={{ pointerEvents: 'none' }}>Back Line</text>

              <line x1={20} y1={Y(boardsY)} x2={W - 20} y2={Y(boardsY)} stroke="#94a3b8" strokeWidth={4} style={{ pointerEvents: 'none' }} />
              {/* Offset toward the diagram's interior (the tee line), not just "below" — the
                  boards line sits near whichever edge is on top for this end, same as the Shot
                  Visualizer's own Back Boards label. */}
              <text x={centerX} y={Y(boardsY) + (Y(teeLineY) > Y(boardsY) ? 16 : -10)} textAnchor="middle" fontSize={9} fill="#94a3b8" style={{ pointerEvents: 'none' }}>Back Boards</text>

              <line x1={centerX} y1={Y(hoggedBandTop)} x2={centerX} y2={Y(boardsY)} stroke="rgba(255,255,255,0.3)" strokeWidth={1.5} strokeDasharray="4 3" style={{ pointerEvents: 'none' }} />

              {/* Broom — fixed at the called position, not interactive; just the reference line the rock's curl is measured from. */}
              <g style={{ pointerEvents: 'none' }}>
                <rect x={broomRenderX - 3} y={Y(teeLineY) - 12} width={6} height={24} rx={2} fill="#facc15" stroke="#78350f" strokeWidth={1} />
              </g>

              {/* Rock — draggable in X always (curl), and in Y too while Draw is
                  selected; a hit type pins Y to hitZoneY instead (see renderRockY).
                  Same white silhouette + colored ring + center dot as the Shot
                  Visualizer's. */}
              <g
                onPointerDown={handleRockPointerDown}
                onPointerMove={handleRockPointerMove}
                onPointerUp={handleRockPointerUp}
                onPointerCancel={handleRockPointerUp}
                style={{ cursor: 'grab', touchAction: 'none' }}
              >
                <circle cx={rockPos.x} cy={Y(renderRockY)} r={18} fill="transparent" />
                <circle cx={rockPos.x} cy={Y(renderRockY)} r={11} fill="#ffffff" stroke="#ec4899" strokeWidth={3} />
                <circle cx={rockPos.x} cy={Y(renderRockY)} r={3} fill="#ec4899" />
              </g>

              {/* Analyze — floats just above the rock and follows it around, same
                  placement the Shot Visualizer's own Set button has above the broom.
                  Records this result and jumps straight into shot analysis. */}
              <g onClick={handleRecordAndAnalyze} style={{ cursor: 'pointer' }}>
                <rect
                  x={Math.min(Math.max(rockPos.x - 28, 22), W - 22 - 56)}
                  y={Math.max(Y(renderRockY) - 34, 24) - 9}
                  width={56}
                  height={18}
                  rx={9}
                  fill="#9333ea"
                  stroke="#6b21a8"
                  strokeWidth={1.5}
                />
                <text
                  x={Math.min(Math.max(rockPos.x, 22 + 28), W - 22 - 28)}
                  y={Math.max(Y(renderRockY) - 34, 24) + 4}
                  textAnchor="middle"
                  fontSize={10}
                  fontWeight="bold"
                  fill="#ffffff"
                  style={{ pointerEvents: 'none' }}
                >
                  Analyze
                </text>
              </g>
            </svg>

            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-slate-400">
              {HIT_ZONES.map(z => (
                <span key={z.zone}>{z.abbr} - {z.label}: {z.zone}</span>
              ))}
            </div>

            {/* The other way out: just record it and come back to the Record Shot
                page to keep working the rest of the form. Analyze (above, on the
                diagram itself) is the other path — records this and jumps straight
                into shot analysis, same as the Shot Visualizer's own Set button. */}
            <button
              type="button"
              onClick={handleRecord}
              className="w-full mt-4 bg-green-500 hover:bg-green-400 text-white py-2.5 rounded-lg font-semibold transition-colors"
            >
              Back to Record Shot
            </button>

            <p className="text-xs text-slate-400 mt-3">
              Starts on the called rock. Pick Draw and drag it to where it actually finished, or pick a hit type if
              it ran through instead — the amber tick shows the broom's called line, and curl is read off the
              rock's distance from that line. <span className="text-slate-500">Analyze</span> (above the rock)
              records this and opens shot analysis; <span className="text-slate-500">Back to Record Shot</span>
              just saves it and returns you here.
            </p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
